import { currencyCodes } from "@client-shared/lib";
import { exportToCsv, formatCurrency, formatDate, type CsvColumn } from "@client-shared/utils";
import type { PurchaseOrderInvoice, PurchaseOrderInvoiceLineDetails, PurchaseOrderInvoiceLines } from "@shared/types/payout";
import type { TFunction } from "i18next";

interface ExportInvoicePOLineItemsParams {
  invoice: PurchaseOrderInvoice;
  line: PurchaseOrderInvoiceLines;
  poDetails: PurchaseOrderInvoiceLineDetails[];
  currencyCode: string;
  vendorId: string;
  showShipToCountry: boolean;
  showKaffeesteuer: boolean;
  t: TFunction;
}

type InvoicePurchaseOrderCsvRow = Record<string, unknown> &
  PurchaseOrderInvoiceLineDetails & {
    orderDate: string;
    shippingFee: number;
    purchaseOrderStatus: string;
    dueDate: string;
    invoiceAmount: number;
    invoiceAccount?: string;
    poNumber: string;
    orderAmountBeforeTax?: number;
    vatRate?: number;
    vatAmount?: number;
    ksAmount?: number;
    shipToCountry?: string;
  };

type InvoiceWithLineCsvRow = Omit<PurchaseOrderInvoice, "invoiceLines"> &
  Omit<PurchaseOrderInvoiceLines, "shipDate"> & {
    orderAmountBeforeTax?: number;
    vatRate?: number;
    vatAmount?: number;
    ksAmount?: number;
    shipToCountry?: string;
  };

export const setExportToCsvFilename = (purchaseOrderId: string): string => `PurchaseOrders_${purchaseOrderId}`;

const formatDateOrEmpty = (val: unknown): string => (val ? formatDate(val as string) : "");

export const getSafeCurrencyCode = (code: string | undefined | null): string => (code && currencyCodes.includes(code) ? code : "USD");

const buildInvoicePurchaseOrderCsvData = (
  invoice: PurchaseOrderInvoice,
  line: PurchaseOrderInvoiceLines,
  poDetails: PurchaseOrderInvoiceLineDetails[],
  vendorId?: string
): InvoicePurchaseOrderCsvRow[] =>
  poDetails.map((data) => ({
    ...data,
    orderDate: line.orderDate,
    shippingFee: line.shippingFee,
    purchaseOrderStatus: line.purchaseOrderStatus,
    dueDate: invoice.dueDate,
    invoiceAmount: invoice.invoiceAmount,
    invoiceAccount: vendorId,
    poNumber: line.purchaseOrderId,
    orderAmountBeforeTax: line.orderAmountBeforeTax,
    vatRate: line.vatRate,
    vatAmount: line.vatAmount,
    ksAmount: line.ksAmount,
    shipToCountry: line.shipToCountry,
  }));

/**
 * Exports purchase order line items to CSV with invoice context
 *
 * @param params - Export parameters including invoice, line items, and translation function
 * @returns void - Triggers browser download
 */
export function exportInvoicePOLineItems({
  invoice,
  line,
  poDetails,
  currencyCode,
  vendorId,
  showShipToCountry,
  showKaffeesteuer,
  t,
}: ExportInvoicePOLineItemsParams): void {
  const invoicePurchaseOrderCsvData = buildInvoicePurchaseOrderCsvData(invoice, line, poDetails, vendorId);
  const formatCurrencyValue = (val: unknown): string => formatCurrency((val as number) || 0, currencyCode);

  const columns: CsvColumn<InvoicePurchaseOrderCsvRow>[] = [
    {
      key: "invoiceId",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.invoiceId"),
    },
    {
      key: "purchaseOrderId",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.purchaseOrderId"),
    },
    {
      key: "dueDate",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.dueDate"),
      formatter: formatDateOrEmpty,
    },
    {
      key: "invoiceDate",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.invoiceDate"),
      formatter: formatDateOrEmpty,
    },
    {
      key: "invoiceAccount",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.invoiceAccount"),
    },
    {
      key: "invoiceAmount",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.invoiceAmount"),
      formatter: formatCurrencyValue,
    },
    {
      key: "invoiceStatus",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.invoiceStatus"),
    },
    {
      key: "purchaseOrderStatus",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.purchaseOrderStatus"),
    },
    {
      key: "shippingFee",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.shippingFee"),
      formatter: formatCurrencyValue,
    },
    {
      key: "purchaseOrderId",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.poNumber"),
    },
    {
      key: "itemNumber",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.itemNumber"),
    },
    {
      key: "quantity",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.quantity"),
    },
    {
      key: "itemName",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.itemName"),
    },
    {
      key: "approvedAmount",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.approvedAmount"),
      formatter: formatCurrencyValue,
    },
    {
      key: "shipDate",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.shipDate"),
      formatter: formatDateOrEmpty,
    },
    {
      key: "orderAmountBeforeTax",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.orderAmountBeforeTax"),
      formatter: (val: unknown) => (val == null ? "" : formatCurrency(val as number, currencyCode)),
    },
    {
      key: "vatRate",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.vatRate"),
      formatter: (val: unknown) => (val == null ? "" : `${val as string}%`),
    },
    {
      key: "vatAmount",
      header: t("components.invoiceDetailDrawer.export.csvHeaders.vatAmount"),
      formatter: (val: unknown) => (val == null ? "" : formatCurrency(val as number, currencyCode)),
    },
    ...(showKaffeesteuer
      ? [
          {
            key: "ksAmount",
            header: t("components.invoiceDetailDrawer.export.csvHeaders.ksAmount"),
            formatter: (val: unknown) => (val == null ? "" : formatCurrency(val as number, currencyCode)),
          },
        ]
      : []),
    ...(showShipToCountry
      ? [
          {
            key: "shipToCountry",
            header: t("components.invoiceDetailDrawer.export.csvHeaders.shipToCountry"),
          },
        ]
      : []),
  ];

  exportToCsv({
    data: invoicePurchaseOrderCsvData,
    filename: setExportToCsvFilename(line.purchaseOrderId),
    columns: columns,
  });
}

export function exportInvoicesWithLines({
  invoices,
  showShipToCountry,
  showKaffeesteuer,
  t,
}: {
  invoices: PurchaseOrderInvoice[];
  showShipToCountry: boolean;
  showKaffeesteuer: boolean;
  t: TFunction;
}): void {
  const rows: InvoiceWithLineCsvRow[] = invoices.flatMap((invoice) =>
    invoice.invoiceLines.map((line) => ({
      invoiceId: invoice.invoiceId,
      poCount: invoice.poCount,
      invoiceAmount: invoice.invoiceAmount,
      invoiceDate: invoice.invoiceDate,
      invoiceStatus: invoice.invoiceStatus,
      currencyCode: invoice.currencyCode,
      purchaseOrderId: line.purchaseOrderId,
      dueDate: invoice.dueDate,
      purchaseOrderStatus: line.purchaseOrderStatus,
      orderAmountBeforeTax: line.orderAmountBeforeTax,
      vatRate: line.vatRate,
      vatAmount: line.vatAmount,
      ksAmount: line.ksAmount,
      shipToCountry: line.shipToCountry,
      orderAmount: line.orderAmount,
      orderDate: line.orderDate,
      lastSettleDate: invoice.lastSettleDate,
      shippingFee: line.shippingFee,
    }))
  );

  const header = (key: string) => t(`finance.invoiceTable.export.csvHeaders.${key}`);
  const formatAmount = (val: unknown, row: InvoiceWithLineCsvRow) =>
    formatCurrency((val as number) || 0, getSafeCurrencyCode(row.currencyCode));

  const columns: CsvColumn<InvoiceWithLineCsvRow>[] = [
    { key: "invoiceId", header: header("invoiceId") },
    { key: "poCount", header: header("poCount") },
    {
      key: "invoiceAmount",
      header: header("invoiceAmount"),
      formatter: formatAmount,
    },
    {
      key: "invoiceDate",
      header: header("invoiceDate"),
      formatter: formatDateOrEmpty,
    },
    { key: "invoiceStatus", header: header("invoiceStatus") },
    { key: "purchaseOrderId", header: header("purchaseOrderId") },
    {
      key: "dueDate",
      header: header("dueDate"),
      formatter: formatDateOrEmpty,
    },
    { key: "purchaseOrderStatus", header: header("purchaseOrderStatus") },
    {
      key: "orderAmount",
      header: header("orderAmount"),
      formatter: formatAmount,
    },
    {
      key: "orderDate",
      header: header("orderDate"),
      formatter: formatDateOrEmpty,
    },
    {
      key: "lastSettleDate",
      header: header("lastSettleDate"),
      formatter: formatDateOrEmpty,
    },
    {
      key: "shippingFee",
      header: header("shippingFee"),
      formatter: formatAmount,
    },
    {
      key: "orderAmountBeforeTax",
      header: header("orderAmountBeforeTax"),
      formatter: (val: unknown, row: InvoiceWithLineCsvRow) =>
        val == null ? "" : formatCurrency(val as number, getSafeCurrencyCode(row.currencyCode)),
    },
    {
      key: "vatRate",
      header: header("vatRate"),
      formatter: (val: unknown) => (val == null ? "" : `${val as string}%`),
    },
    {
      key: "vatAmount",
      header: header("vatAmount"),
      formatter: (val: unknown, row: InvoiceWithLineCsvRow) =>
        val == null ? "" : formatCurrency(val as number, getSafeCurrencyCode(row.currencyCode)),
    },
    ...(showKaffeesteuer
      ? [
          {
            key: "ksAmount",
            header: header("ksAmount"),
            formatter: (val: unknown, row: InvoiceWithLineCsvRow) =>
              val == null ? "" : formatCurrency(val as number, getSafeCurrencyCode(row.currencyCode)),
          },
        ]
      : []),
    ...(showShipToCountry ? [{ key: "shipToCountry", header: header("shipToCountry") }] : []),
  ];

  exportToCsv({
    data: rows,
    filename: "PurchaseOrders",
    columns: columns,
  });
}
